import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PaymentMethodsPanel = ({ isReadOnly = false }) => {
  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: 1,
      type: 'card',
      cardNumber: '**** **** **** 4532',
      expiryDate: '12/25',
      cardholderName: 'John Doe',
      isDefault: true,
      isVerified: true
    }
  ]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [newMethod, setNewMethod] = useState({
    type: 'card',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: ''
  });

  const bankDetails = {
    payTo: 'Pacific RBS Limited',
    bankName: 'Bank South Pacific (BSP)',
    bsb: '088-950',
    branch: 'BSP Haus',
    accountNumber: '7017383691'
  };

  const handleCopyToClipboard = (text, field) => {
    navigator?.clipboard?.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleAddMethod = () => {
    if (isReadOnly) return;
    if (newMethod?.cardNumber && newMethod?.expiryDate && newMethod?.cardholderName) {
      const maskedNumber = `**** **** **** ${newMethod?.cardNumber?.slice(-4)}`;
      setPaymentMethods([...paymentMethods, {
        id: Date.now(),
        type: newMethod?.type,
        cardNumber: maskedNumber,
        expiryDate: newMethod?.expiryDate,
        cardholderName: newMethod?.cardholderName,
        isDefault: false,
        isVerified: false
      }]);
      setNewMethod({ type: 'card', cardNumber: '', expiryDate: '', cvv: '', cardholderName: '' });
      setShowAddForm(false);
    }
  };

  const handleDeleteMethod = (id) => {
    if (isReadOnly) return;
    setPaymentMethods(paymentMethods?.filter(method => method?.id !== id));
  };

  const handleSetDefault = (id) => {
    if (isReadOnly) return;
    setPaymentMethods(paymentMethods?.map(method => ({
      ...method,
      isDefault: method?.id === id
    })));
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Payment Methods</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage your payment options</p>
        </div>
        {!isReadOnly && (
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            Add Method
          </Button>
        )}
      </div>

      {/* Add Payment Method Form */}
      {showAddForm && !isReadOnly && (
        <div className="bg-muted/30 border border-border rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Add New Payment Method</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select
              label="Payment Type"
              value={newMethod?.type}
              onChange={(value) => setNewMethod({ ...newMethod, type: value })}
              options={[
                { value: 'card', label: 'Credit/Debit Card' },
                { value: 'bank', label: 'Bank Transfer' }
              ]}
            />
            <Input
              label="Cardholder Name"
              value={newMethod?.cardholderName}
              onChange={(e) => setNewMethod({ ...newMethod, cardholderName: e?.target?.value })}
              placeholder="John Doe"
            />
            <Input
              label="Card Number"
              value={newMethod?.cardNumber}
              onChange={(e) => setNewMethod({ ...newMethod, cardNumber: e?.target?.value })}
              placeholder="1234 5678 9012 3456"
              maxLength={16}
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Expiry Date"
                value={newMethod?.expiryDate}
                onChange={(e) => setNewMethod({ ...newMethod, expiryDate: e?.target?.value })}
                placeholder="MM/YY"
              />
              <Input
                label="CVV"
                type="password"
                value={newMethod?.cvv}
                onChange={(e) => setNewMethod({ ...newMethod, cvv: e?.target?.value })}
                placeholder="123"
                maxLength={3}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button size="sm" onClick={handleAddMethod}>Save Method</Button>
            <Button size="sm" variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Saved Payment Methods */}
      <div className="space-y-3 mb-6">
        {paymentMethods?.map((method) => (
          <div
            key={method?.id}
            className={`border rounded-lg p-4 ${
              method?.isDefault ? 'border-primary bg-primary/5' : 'border-border'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded">
                  <Icon name="CreditCard" size={20} className="text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-foreground">{method?.cardNumber}</p>
                    {method?.isDefault && (
                      <span className="text-xs px-2 py-0.5 bg-primary text-primary-foreground rounded-full">
                        Default
                      </span>
                    )}
                    {method?.isVerified && (
                      <Icon name="CheckCircle" size={14} className="text-success" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {method?.cardholderName} • Expires {method?.expiryDate}
                  </p>
                </div>
              </div>
              {!isReadOnly && (
                <div className="flex gap-2">
                  {!method?.isDefault && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleSetDefault(method?.id)}
                    >
                      Set Default
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    iconName="Trash2"
                    onClick={() => handleDeleteMethod(method?.id)}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Bank Transfer Details */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Building2" size={20} className="text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Bank Transfer Payment</h3>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Pay To</p>
              <p className="text-sm font-medium text-foreground">{bankDetails?.payTo}</p>
            </div>
            <Button
              size="xs"
              variant="ghost"
              iconName={copiedField === 'payTo' ? 'Check' : 'Copy'}
              onClick={() => handleCopyToClipboard(bankDetails?.payTo, 'payTo')}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Bank</p>
              <p className="text-sm font-medium text-foreground">{bankDetails?.bankName}</p>
            </div>
            <Button
              size="xs"
              variant="ghost"
              iconName={copiedField === 'bank' ? 'Check' : 'Copy'}
              onClick={() => handleCopyToClipboard(bankDetails?.bankName, 'bank')}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">BSB</p>
                <p className="text-sm font-medium text-foreground">{bankDetails?.bsb}</p>
              </div>
              <Button
                size="xs"
                variant="ghost"
                iconName={copiedField === 'bsb' ? 'Check' : 'Copy'}
                onClick={() => handleCopyToClipboard(bankDetails?.bsb, 'bsb')}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Branch</p>
                <p className="text-sm font-medium text-foreground">{bankDetails?.branch}</p>
              </div>
              <Button
                size="xs"
                variant="ghost"
                iconName={copiedField === 'branch' ? 'Check' : 'Copy'}
                onClick={() => handleCopyToClipboard(bankDetails?.branch, 'branch')}
              />
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div>
              <p className="text-xs text-muted-foreground">Account Number</p>
              <p className="text-base font-bold text-foreground">{bankDetails?.accountNumber}</p>
            </div>
            <Button
              size="xs"
              variant="ghost"
              iconName={copiedField === 'account' ? 'Check' : 'Copy'}
              onClick={() => handleCopyToClipboard(bankDetails?.accountNumber, 'account')}
            />
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-border flex items-center gap-2">
          <Icon name="Shield" size={14} className="text-success" />
          <p className="text-xs text-muted-foreground">PCI DSS Compliant • Secure Payment Processing</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodsPanel;